// Addresses of the public pages: lowercase only (owner's rule, 2026-09-28 -
// "in URLs only small letters, capitals never"). A province is its two-letter
// code (/en/qc), a city its name in plain letters and hyphens (montreal,
// trois-rivieres, l-assomption), a listing its slug. Any other spelling - /en/QC,
// /en/qc/Montr%C3%A9al, /en/qc/Trois%20Rivi%C3%A8res - answers one 301 to the
// lowercase address (proxy.ts), so a page has one address and every link,
// sitemap entry and canonical names that one.
//
// Pure functions: proxy.ts runs this before any page, without a database.

import { langHome, langPrefix } from '@/lib/locale-path';
import { PROVINCES } from '@/lib/provinces';

const CODES = new Set<string>(PROVINCES.map((p) => p.code.toLowerCase()));

/** A place or a slug as an address segment: a-z, 0-9 and single hyphens. */
export function placeSlug(value: string): string {
  let text = value;
  try {
    text = decodeURIComponent(value);
  } catch {
    // Not percent-encoded after all.
  }
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The SQL twin of placeSlug, for matching a stored city name to its address. */
export const placeSlugSql = (column: string) => `trim(both '-' from regexp_replace(lower(f_unaccent(${column})), '[^a-z0-9]+', '-', 'g'))`;

export const isProvinceCode = (segment: string) => CODES.has(segment.toLowerCase());

// Segments that name a rentals branch, in either language.
const RENTAL_ROOTS = new Set(['rentals', 'locations']);

/**
 * The lowercase address for a public path, or null when it already is one.
 * Place segments (a province, a city, a listing) are also stripped of accents
 * and spaces; every other path is only lowercased. The query string is not
 * part of this - filter values are not addresses.
 */
export function lowercasePath(pathname: string): string | null {
  const parts = pathname.split('/');
  // ['', lang, ...]
  const lang = (parts[1] || '').toLowerCase();
  if (lang !== 'en' && lang !== 'fr') {
    const lower = pathname.toLowerCase();
    return lower === pathname ? null : lower;
  }
  const out = parts.map((p) => {
    try {
      return decodeURIComponent(p) === p ? p.toLowerCase() : p;
    } catch {
      return p.toLowerCase();
    }
  });
  out[1] = lang;
  // A place path: /lang/<province>[/<city>[/<listing>]] or the same under
  // /lang/rentals (or locations).
  let at = 2;
  if (RENTAL_ROOTS.has((out[2] || '').toLowerCase())) at = 3;
  if (out[at] && isProvinceCode(out[at]) && out.length <= at + 3) {
    // A listing (province, city, slug) is settled by its page, which knows
    // from the database whether it belongs under the rentals branch: the
    // whole address is put right there, in one redirect instead of two.
    if (parts[at + 2]) {
      const head = out.slice(0, at).join('/');
      const original = parts.slice(0, at).join('/');
      return head === original ? null : [head, ...parts.slice(at)].join('/');
    }
    for (let i = at; i < out.length && i <= at + 1; i++) if (parts[i]) out[i] = placeSlug(parts[i]);
  }
  const result = out.join('/').replace(/\/{2,}/g, '/');
  return result === pathname ? null : result;
}

type Placed = { province: string; city: string; slug: string; listing_type?: string | null };

/**
 * A listing's address: /en/qc/montreal/<slug> for a sale, and under the
 * rentals branch for a rental - /en/rentals/qc/montreal/<slug>,
 * /fr/locations/qc/montreal/<slug> (owner's plan, 2026-09-28).
 */
export function listingPathFor(lang: string, listing: Placed): string {
  const place = `${listing.province.toLowerCase()}/${placeSlug(listing.city)}/${placeSlug(listing.slug)}`;
  if (listing.listing_type === 'rent') return `${langPrefix(lang)}/${lang === 'fr' ? 'locations' : 'rentals'}/${place}`;
  return `${langPrefix(lang)}/${place}`;
}

/**
 * A province's or a city's page for sale or for rent, from the stored names -
 * lowercase, as every address (lib/rental-seo.ts has the same for pages that
 * already hold a place; this one is small enough for the browser).
 */
export function placeHref(lang: string, deal: 'sale' | 'rent', place: { province: string; city?: string }): string {
  const root = deal === 'rent' ? `${langPrefix(lang)}/${lang === 'fr' ? 'locations' : 'rentals'}` : langPrefix(lang);
  const province = place.province.toLowerCase();
  return place.city ? `${root}/${province}/${placeSlug(place.city)}` : `${root}/${province}`;
}
